/**
 * Localwineriesscreen.jsx
 *
 * Map section requires:
 *   npm install react-leaflet leaflet
 *
 * Add to your global CSS / index.html <head>:
 *   <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
 *
 * Or import it once at the top of your app:
 *   import 'leaflet/dist/leaflet.css'
 */

import React, { useState, useEffect, useRef } from 'react'
import styled, { keyframes, createGlobalStyle } from 'styled-components'
import { motion, AnimatePresence } from 'framer-motion'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'
import { SliderDataFourteen } from '../data/SliderData'
import Hero from '../components/Hero'

/* ─── Fonts ──────────────────────────────────────────────────── */
const FontLoader = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Bebas+Neue&family=Jost:wght@300;400;500;600&display=swap');
  .leaflet-container { font-family: 'Jost', sans-serif !important; }
  .leaflet-popup-content-wrapper {
    background: #1c1008 !important;
    border: 1px solid rgba(201,147,58,0.35) !important;
    border-radius: 10px !important;
    box-shadow: 0 8px 40px rgba(0,0,0,0.7) !important;
    color: #f5ede0 !important;
  }
  .leaflet-popup-tip { background: #1c1008 !important; }
  .leaflet-popup-close-button { color: #c9933a !important; font-size: 18px !important; right: 8px !important; top: 6px !important; }
  .leaflet-popup-close-button:hover { color: #f5ede0 !important; }
  .leaflet-control-zoom a {
    background: #1c1008 !important;
    color: #c9933a !important;
    border-color: rgba(201,147,58,0.3) !important;
  }
  .leaflet-control-zoom a:hover { background: #2a1810 !important; }
  .leaflet-control-attribution { background: rgba(12,8,4,0.7) !important; color: #8a7560 !important; }
  .leaflet-control-attribution a { color: #c9933a !important; }
`;

/* ─── Design Tokens ──────────────────────────────────────────── */
const amber   = '#c9933a'
const amberLt = '#e8b55a'
const cream   = '#f5ede0'
const ink     = '#0e0a07'
const mist    = '#1c1008'
const warm    = '#2a1810'
const bodyClr = 'rgba(196,180,154,0.9)'
const display = `'Cormorant Garamond', Georgia, serif`
const sans    = `'Jost', sans-serif`
const slab    = `'Bebas Neue', sans-serif`

/* ─── Keyframes ──────────────────────────────────────────────── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(32px); }
  to   { opacity: 1; transform: translateY(0); }
`
const lineGrow = keyframes`
  from { width: 0; }
  to   { width: 72px; }
`
const marquee = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`
const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(201,147,58,0.6); }
  50%       { box-shadow: 0 0 0 8px rgba(201,147,58,0); }
`

/* ─── Motion Variants ────────────────────────────────────────── */
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.09 } } }
const fadeSlide = {
  hidden: { opacity: 0, y: 28 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
}

/* ─── Winery Data ────────────────────────────────────────────── */
const WINERIES = [
  {
    id: 1,
    name: 'Dynamis Estate Wines',
    address: '1004 Highland Rd, Jonesville, NC',
    lat: 36.2000, lng: -80.8149,
    rating: 4.8, reviews: 81,
    tags: ['Reservation Required', '1,640 ft Elevation', 'Bordeaux Varietals'],
    notes: "1,500-acre private estate with a breathtaking 2.5-mile gated mountain drive. Nominated for Wine Enthusiast's American Winery of the Year.",
    phone: '336-468-6702',
    website: 'https://www.dynamisestatewines.com',
    hours: 'Wed–Sat 11–5 PM · Sun 12–5 PM · Reservation Required',
    dist: '~4 mi',
  },
  {
    id: 2,
    name: 'Piccione Vineyards',
    address: '2364 Cedar Forest Rd, Ronda, NC',
    lat: 36.1773, lng: -80.8935,
    rating: 4.5, reviews: 268,
    tags: ['Sicilian Heritage', 'Italian Varietals', 'Wedding Venue'],
    notes: 'Italian-owned Swan Creek estate. Montepulciano, Sangiovese, Vermentino & a sparkling méthode champenoise Chardonnay.',
    phone: '336-571-1024',
    website: 'https://www.piccionevineyards.com',
    hours: 'Mon–Sun 12–6 PM',
    dist: '~4 mi',
  },
  {
    id: 3,
    name: 'Laurel Gray Vineyards',
    address: '5726 Old US Hwy 421, Hamptonville, NC',
    lat: 36.1341, lng: -80.8392,
    rating: 4.8, reviews: 173,
    tags: ['Family-Owned', 'Pond & Patio', 'Food Trucks'],
    notes: 'Beautiful covered patio overlooking a pond. Sweet and dry wines, outdoor seating, and a warm family history.',
    phone: '336-468-9463',
    website: 'https://www.laurelgray.com',
    hours: 'Wed–Fri 10–5 PM · Sat 10–5 PM · Sun 1–5 PM',
    dist: '~3 mi',
  },
  {
    id: 4,
    name: 'Raffaldini Vineyards',
    address: '450 Groce Rd, Ronda, NC',
    lat: 36.1789, lng: -80.8916,
    rating: 4.3, reviews: 666,
    tags: ['Italian Style', 'Tuscan Vibes', 'Wedding Venue'],
    notes: 'Stunning Tuscan-inspired estate with sweeping Blue Ridge Mountain views. Italian varietals and an annual Fiesta Italiana festival.',
    phone: '336-835-9463',
    website: 'https://www.raffaldini.com',
    hours: 'Mon, Wed–Sat 11–5 PM · Sun 12–5 PM',
    dist: '~4 mi',
  },
  {
    id: 5,
    name: 'Slightly Askew Winery',
    address: '913 N Bridge St, Elkin, NC',
    lat: 36.259, lng: -80.8482,
    rating: 4.9, reviews: 139,
    tags: ['Urban', 'Sweet & Dry', 'Cozy Patio'],
    notes: 'Downtown Elkin. South American-style dry wines plus fruit specialties.',
    phone: '336-835-2700',
    website: 'https://www.slightlyaskewwines.com',
    hours: 'Thu–Sat 1–6 PM · Sun 1–5 PM',
    dist: '~10 mi',
  },
  {
    id: 6,
    name: 'Adagio Vineyards',
    address: '139 Benge Dr, Elkin, NC',
    lat: 36.2791, lng: -80.8603,
    rating: 4.9, reviews: 66,
    tags: ['European Dry', 'Kid-Friendly', 'Artisan'],
    notes: 'Violin-making owners. Fine dry wines in an elegant tasting room.',
    phone: '336-258-2333',
    website: 'https://www.adagiovineyards.com',
    hours: 'Mon, Thu–Sun · 12–5 PM',
    dist: '~13 mi',
  },
  {
    id: 7,
    name: 'Elkin Creek Vineyard',
    address: '318 Elkin Creek Mill Rd, Elkin, NC',
    lat: 36.2776, lng: -80.8735,
    rating: 4.4, reviews: 148,
    tags: ['Creekside', 'Pizza Sundays', 'Cabins'],
    notes: 'Historic grist mill site. Wood-fired pizza Sundays. Waterfall on property.',
    phone: '336-526-5119',
    website: 'https://www.elkincreekvineyard.com',
    hours: 'Thu–Fri 2–5 PM · Sat–Sun 12–5 PM',
    dist: '~14 mi',
  },
  {
    id: 8,
    name: 'Carolina Heritage Vineyard',
    address: '170 Heritage Vines Way, Elkin, NC',
    lat: 36.2778, lng: -80.7656,
    rating: 4.9, reviews: 132,
    tags: ['Certified Organic', 'Solar-Powered', 'Beer on Tap'],
    notes: "NC's first USDA Certified Organic vineyard. First solar-powered winery.",
    phone: '910-703-3395',
    website: 'https://www.carolinaheritagevineyards.com',
    hours: 'Fri 5–9 PM · Sat 12–8 PM · Sun 1–6 PM',
    dist: '~14 mi',
  },
  {
    id: 9,
    name: 'Grassy Creek Vineyard',
    address: '235 Chatham Cottage Ln, State Road, NC',
    lat: 36.294, lng: -80.8622,
    rating: 4.9, reviews: 204,
    tags: ['Dog-Friendly', 'Hiking', 'Former Dairy Farm'],
    notes: 'Former dairy farm. 4-mile hike to Carter Falls from the vineyard.',
    phone: '336-835-2458',
    website: 'https://www.grassycreekvineyard.com',
    hours: 'Mon, Thu–Sat 12–6 PM · Sun 1–5 PM',
    dist: '~15 mi',
  },
  {
    id: 10,
    name: 'Stony Knoll Vineyards',
    address: '1143 Stony Knoll Rd, Dobson, NC',
    lat: 36.3066, lng: -80.6777,
    rating: 4.9, reviews: 101,
    tags: ['Century Farm', 'Reserve Wines', 'Scenic Views'],
    notes: 'NC Century Farm since 1896. Award-winning reserve portfolio.',
    phone: '336-374-5752',
    website: 'https://www.stonyknollvineyards.com',
    hours: 'Mon, Thu–Sat 10–6 PM',
    dist: '~21 mi',
  },
  {
    id: 11,
    name: 'Golden Road Vineyards',
    address: '119 Wine Wy, State Road, NC',
    lat: 36.3636, lng: -80.8725,
    rating: 4.9, reviews: 80,
    tags: ['Mountain Views', 'Fire Pits', 'RV-Friendly'],
    notes: '25 rolling acres. Fire pits, cabins, and sweeping mountain vistas.',
    phone: '336-466-7264',
    website: 'https://www.grvwines.com',
    hours: 'Fri 2–7 PM · Sat 12–5 PM · Sun 12–6 PM',
    dist: '~22 mi',
  },
  {
    id: 12,
    name: 'Shelton Vineyards',
    address: '286 Cabernet Ln, Dobson, NC',
    lat: 36.3645, lng: -80.7686,
    rating: 4.6, reviews: 881,
    tags: ['Largest NC Estate', 'Restaurant', 'Hampton Inn'],
    notes: "NC's largest family-owned estate winery. Harvest Grill restaurant on site.",
    phone: '336-366-4724',
    website: 'https://www.sheltonvineyards.com',
    hours: 'Mon–Sun 11–6 PM',
    dist: '~25 mi',
  },
  {
    id: 13,
    name: 'Jones von Drehle',
    address: '964 Old Railroad Grade Rd, Thurmond, NC',
    lat: 36.3739, lng: -80.9427,
    rating: 4.3, reviews: 96,
    tags: ['Blue Ridge Views', 'Concert Stage', '30 Acres of Vines'],
    notes: 'Weekend concerts on a full outdoor amphitheatre stage. All dry wines.',
    phone: '336-874-2800',
    website: 'https://www.jonesvondrehle.com',
    hours: 'Wed–Sat 11–5 PM · Sun 12–5 PM',
    dist: '~26 mi',
  },
  {
    id: 14,
    name: 'JOLO Vineyards',
    address: '219 Jolo Winery Ln, Pilot Mountain, NC',
    lat: 36.36, lng: -80.51,
    rating: 4.8, reviews: 320,
    tags: ['90-Acre Estate', 'Pilot Mountain Views', 'Fine Dining'],
    notes: '90 stunning acres. End Posts Restaurant serves lunch and dinner.',
    phone: '336-614-0030',
    website: 'https://www.jolovineyards.com',
    hours: 'Thu–Sun (call for hours)',
    dist: '~35 mi',
  },
  {
    id: 15,
    name: 'Round Peak Vineyards',
    address: '765 Round Peak Church Rd, Mt Airy, NC',
    lat: 36.4997, lng: -80.7684,
    rating: 4.8, reviews: 243,
    tags: ['Live Music Fridays', 'Disc Golf', 'Mountain Views'],
    notes: 'Original location of Skull Camp Brewery. Patio aligns perfectly with the vines.',
    phone: '336-352-5595',
    website: 'https://www.roundpeak.com',
    hours: 'Fri 12–8 PM · Sat 11–6 PM · Sun 12–5 PM',
    dist: '~38 mi',
  },
]

/* ─── Custom Leaflet Icons ───────────────────────────────────── */
const makeIcon = (active = false) => L.divIcon({
  className: '',
  html: `
    <div style="
      width:${active ? 38 : 28}px;
      height:${active ? 38 : 28}px;
      background:${active ? '#c9933a' : '#1c1008'};
      border:2px solid #c9933a;
      border-radius:50%;
      display:flex;
      align-items:center;
      justify-content:center;
      box-shadow:0 4px 16px rgba(0,0,0,0.5)${active ? ',0 0 0 5px rgba(201,147,58,0.25)' : ''};
      transition:all 0.25s;
    ">
      <svg width="${active ? 16 : 12}" height="${active ? 16 : 12}" viewBox="0 0 20 20" fill="${active ? '#0e0a07' : '#c9933a'}">
        <path d="M10 0C6.13 0 3 3.13 3 7c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 10 4.5a2.5 2.5 0 0 1 0 5z"/>
      </svg>
    </div>
  `,
  iconSize: [active ? 38 : 28, active ? 38 : 28],
  iconAnchor: [active ? 19 : 14, active ? 38 : 28],
  popupAnchor: [0, active ? -38 : -28],
})

const homeIcon = L.divIcon({
  className: '',
  html: `
    <div style="
      width:42px; height:42px;
      background:#c9933a;
      border:3px solid #f5ede0;
      border-radius:50%;
      display:flex; align-items:center; justify-content:center;
      box-shadow:0 4px 20px rgba(0,0,0,0.6),0 0 0 6px rgba(201,147,58,0.2);
    ">
      <span style="font-size:18px; line-height:1;">🏡</span>
    </div>
  `,
  iconSize: [42, 42],
  iconAnchor: [21, 42],
  popupAnchor: [0, -42],
})

/* ─── Map fly-to helper ──────────────────────────────────────── */
const FlyTo = ({ lat, lng }) => {
  const map = useMap()
  useEffect(() => {
    if (lat && lng) map.flyTo([lat, lng], 14, { duration: 1.2 })
  }, [lat, lng, map])
  return null
}

/* ═══════════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════════ */
const Localwineriesscreen = () => {
  const [active, setActive]     = useState(null) // winery id
  const [flyTarget, setFlyTarget] = useState(null)
  const listRef = useRef(null)

  const selectWinery = (w) => {
    setActive(w.id)
    setFlyTarget({ lat: w.lat, lng: w.lng })
    // scroll list item into view
    const el = listRef.current?.querySelector(`[data-id="${w.id}"]`)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  const activeWinery = WINERIES.find(w => w.id === active)

  return (
    <>
      <FontLoader />
      <PageWrapper>
        <Hero slides={SliderDataFourteen} />

        {/* ── Intro ─────────────────────────────────────────── */}
        <IntroSection>
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="show"
          >
            <EyebrowText as={motion.p} variants={fadeSlide}>
              Yadkin Valley · North Carolina
            </EyebrowText>
            <IntroHeading as={motion.h2} variants={fadeSlide}>
              Explore the abundance of wineries woven into our Blue Ridge foothills.
            </IntroHeading>
            <GoldDivider as={motion.span} variants={fadeSlide} />
            <IntroBody as={motion.p} variants={fadeSlide}>
              With seven counties and more than 35 local wineries, the Yadkin Valley has emerged
              as North Carolina's premier wine country — known not for sweet muscadines, but for
              dry, elegant European-style varietals paired with breathtaking mountain views.
            </IntroBody>
          </motion.div>
        </IntroSection>

        {/* ── Link Cards ────────────────────────────────────── */}
        <LinksSection>
          <LinksGrid
            as={motion.div}
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {[
              {
                num: '01 / 03',
                title: 'Ten of Our Finest Selections',
                meta: 'Curated by locals and guests alike — the Yadkin Valley\'s most celebrated tasting rooms, all in one place.',
                href: 'https://www.yelp.com/search?cflt=wineries&find_loc=Yadkin+County%2C+NC',
                label: 'Explore on Yelp',
              },
              {
                num: '02 / 03',
                title: 'The Full Winery Map',
                meta: 'Every estate, vineyard, and tasting room across all seven counties of the Yadkin Valley — your complete guide.',
                href: 'https://www.yadkinvalleync.com/attractions/?subcategories=wineries',
                label: 'Browse the Map',
              },
              {
                num: '03 / 03',
                title: 'A Full Day on the Wine Trail',
                meta: 'Follow the Surry County wine trail through rolling vineyards, with stops planned so you can sip from morning to sunset.',
                href: 'https://www.nctripping.com/surry-county-wine-trail-vineyards/',
                label: 'Plan Your Trail Day',
              },
            ].map((card, i) => (
              <LinkCard key={i} as={motion.a} variants={fadeSlide} href={card.href} target="_blank" rel="noopener noreferrer">
                <div>
                  <CardNumber>{card.num}</CardNumber>
                  <CardTitle>{card.title}</CardTitle>
                  <CardMeta>{card.meta}</CardMeta>
                </div>
                <CardArrow>{card.label}</CardArrow>
              </LinkCard>
            ))}
          </LinksGrid>
        </LinksSection>

        {/* ══════════════════════════════════════════════════════
            MAP SECTION
        ══════════════════════════════════════════════════════ */}
        <MapSectionOuter>
          <MapSectionNoise />
          <MapSectionGlow />

          <MapSectionInner>
            {/* Header */}
            <MapHeader
              as={motion.div}
              variants={stagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            >
              <MapEyebrow as={motion.p} variants={fadeSlide}>
                Interactive Wine Trail Map
              </MapEyebrow>
              <MapTitle as={motion.h2} variants={fadeSlide}>
                15 Wineries Near<br /><em>Little Heaven</em>
              </MapTitle>
              <MapSubtitle as={motion.p} variants={fadeSlide}>
                All within 45 miles · Click any winery to explore
              </MapSubtitle>
            </MapHeader>

            {/* Split layout: sidebar + map */}
            <MapLayout>
              {/* ── Sidebar list ── */}
              <WineryList ref={listRef}>
                {/* Home anchor */}
                <HomeCard>
                  <HomeIcon>🏡</HomeIcon>
                  <HomeText>
                    <HomeLabel>Your Home Base</HomeLabel>
                    <HomeName>Little Heaven B&amp;B</HomeName>
                    <HomeAddr>1065 Tuckda Way, Hamptonville, NC 27020</HomeAddr>
                  </HomeText>
                </HomeCard>

                <ListDivider />

                {WINERIES.map((w) => (
                  <WineryCard
                    key={w.id}
                    data-id={w.id}
                    $active={active === w.id}
                    onClick={() => selectWinery(w)}
                    as={motion.div}
                    whileHover={{ x: 4, transition: { duration: 0.2 } }}
                  >
                    <WineryCardTop>
                      <WineryNum>{String(w.id).padStart(2, '0')}</WineryNum>
                      <WineryInfo>
                        <WineryName $active={active === w.id}>{w.name}</WineryName>
                        <WineryAddr>{w.address}</WineryAddr>
                        <WineryTagRow>
                          {w.tags.slice(0, 2).map(t => (
                            <WineryTag key={t}>{t}</WineryTag>
                          ))}
                          <WineryDist>{w.dist}</WineryDist>
                        </WineryTagRow>
                      </WineryInfo>
                      <WineryRating>
                        <RatingStar>★</RatingStar>
                        <RatingVal>{w.rating}</RatingVal>
                      </WineryRating>
                    </WineryCardTop>

                    <AnimatePresence>
                      {active === w.id && (
                        <WineryExpanded
                          as={motion.div}
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        >
                          <ExpandedNotes>{w.notes}</ExpandedNotes>
                          <ExpandedMeta>
                            <MetaRow><MetaIcon>🕐</MetaIcon>{w.hours}</MetaRow>
                            <MetaRow><MetaIcon>📞</MetaIcon>{w.phone}</MetaRow>
                          </ExpandedMeta>
                          <ExpandedActions>
                            <ExpandBtn
                              href={w.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                            >
                              Visit Website ↗
                            </ExpandBtn>
                            <ExpandBtn
                              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(w.address)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              $outline
                              onClick={e => e.stopPropagation()}
                            >
                              Get Directions ↗
                            </ExpandBtn>
                          </ExpandedActions>
                        </WineryExpanded>
                      )}
                    </AnimatePresence>
                  </WineryCard>
                ))}
              </WineryList>

              {/* ── Map ── */}
              <MapWrap>
                <MapContainer
                  center={[36.35, -80.78]}
                  zoom={10}
                  style={{ width: '100%', height: '100%' }}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                    maxZoom={19}
                  />

                  {/* Home marker */}
                  <Marker position={[36.1685, -80.8471]} icon={homeIcon}>
                    <Popup>
                      <PopupInner>
                        <PopupLabel>Your Home Base</PopupLabel>
                        <PopupName>Little Heaven B&amp;B</PopupName>
                        <PopupSub>1065 Tuckda Way, Hamptonville, NC 27020</PopupSub>
                      </PopupInner>
                    </Popup>
                  </Marker>

                  {/* Winery markers */}
                  {WINERIES.map((w) => (
                    <Marker
                      key={w.id}
                      position={[w.lat, w.lng]}
                      icon={makeIcon(active === w.id)}
                      eventHandlers={{ click: () => selectWinery(w) }}
                    >
                      <Popup>
                        <PopupInner>
                          <PopupLabel>
                            {w.tags[0]} · ★ {w.rating}
                          </PopupLabel>
                          <PopupName>{w.name}</PopupName>
                          <PopupSub>{w.address}</PopupSub>
                          <PopupHours>{w.hours}</PopupHours>
                          <PopupLinkRow>
                            <PopupLink href={w.website} target="_blank" rel="noopener noreferrer">
                              Website ↗
                            </PopupLink>
                            <PopupLink
                              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(w.address)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Directions ↗
                            </PopupLink>
                          </PopupLinkRow>
                        </PopupInner>
                      </Popup>
                    </Marker>
                  ))}

                  {/* Fly to selected */}
                  {flyTarget && <FlyTo lat={flyTarget.lat} lng={flyTarget.lng} />}
                </MapContainer>

                {/* Map legend */}
                <MapLegend>
                  <LegendItem>
                    <LegendDot $home />
                    <LegendText>Little Heaven B&amp;B</LegendText>
                  </LegendItem>
                  <LegendItem>
                    <LegendDot />
                    <LegendText>Winery</LegendText>
                  </LegendItem>
                  <LegendItem>
                    <LegendDot $active />
                    <LegendText>Selected</LegendText>
                  </LegendItem>
                </MapLegend>
              </MapWrap>
            </MapLayout>
          </MapSectionInner>
        </MapSectionOuter>

        {/* ── Wine Tour Section ─────────────────────────────── */}
        <TourSection>
          <TourInner>
            <TourEyebrow>Exclusive Service</TourEyebrow>
            <TourTitle>Let Someone Else Handle the Driving</TourTitle>
            <TourDivider />
            <TourLayout>
              <TourImagePane>
                <img
                  src="https://i.postimg.cc/j5LqF0MP/77153342-c177-46f9-9117-ee29a00617a7.png"
                  alt="Elkin Vine Line wine tour vehicle"
                />
              </TourImagePane>
              <TourTextPane>
                <TourBadge>Wine Tour Service</TourBadge>
                <TourBody>
                  Enjoy a completely carefree afternoon in wine country with our pick-up
                  and drop-off service at each stop. Choose 2–3 wineries from our curated
                  list of 8 local favorites — we'll handle the driving so you can focus on
                  the tasting.
                </TourBody>
                <TourFeatures>
                  <TourFeatureItem>Choose 2 or 3 wineries from our curated list of 8</TourFeatureItem>
                  <TourFeatureItem>Door-to-door pick-up and drop-off at every stop</TourFeatureItem>
                  <TourFeatureItem>No designated driver needed — sip freely</TourFeatureItem>
                  <TourFeatureItem>Intimate group experience through the valley</TourFeatureItem>
                </TourFeatures>
              </TourTextPane>
            </TourLayout>
          </TourInner>
        </TourSection>

        {/* ── Marquee Strip ─────────────────────────────────── */}
        <MarqueeStrip>
          <MarqueeInner>
            {Array(6).fill('YADKIN VALLEY · NORTH CAROLINA WINE COUNTRY · LITTLE HEAVEN B&B ·').map((t, i) => (
              <MarqueeText key={i}>{t}</MarqueeText>
            ))}
          </MarqueeInner>
        </MarqueeStrip>

      </PageWrapper>
    </>
  )
}

export default Localwineriesscreen

/* ═══════════════════════════════════════════════════════════════
   STYLED COMPONENTS
═══════════════════════════════════════════════════════════════ */

const PageWrapper = styled.div`
  background: #0e0a07;
  color: ${cream};
  font-family: ${sans};
  overflow-x: hidden;
`

/* ── Intro ────────────────────────────────────────────────────── */
const IntroSection = styled.section`
  position: relative;
  padding: 90px 24px 60px;
  text-align: center;
  background: linear-gradient(180deg, #0e0a07 0%, #1a0f08 100%);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse 70% 50% at 50% 0%, rgba(180,120,40,0.12) 0%, transparent 70%);
    pointer-events: none;
  }
`

const EyebrowText = styled.p`
  font-family: ${sans};
  font-size: 0.7rem;
  letter-spacing: 0.35em;
  text-transform: uppercase;
  color: ${amber};
  margin-bottom: 18px;
`

const IntroHeading = styled.h2`
  font-family: ${display};
  font-size: clamp(1.6rem, 3.5vw, 2.8rem);
  font-weight: 400;
  line-height: 1.4;
  max-width: 780px;
  margin: 0 auto 24px;
  color: ${cream};
`

const GoldDivider = styled.span`
  display: block;
  height: 1px;
  width: 72px;
  background: linear-gradient(90deg, transparent, ${amber}, transparent);
  margin: 0 auto 32px;
`

const IntroBody = styled.p`
  font-family: ${sans};
  font-size: 1.05rem;
  line-height: 1.8;
  max-width: 660px;
  margin: 0 auto;
  color: ${bodyClr};
`

/* ── Links ────────────────────────────────────────────────────── */
const LinksSection = styled.section`
  padding: 20px 24px 90px;
  background: linear-gradient(180deg, #1a0f08 0%, #120b05 100%);
`

const LinksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;
  max-width: 1100px;
  margin: 0 auto;
`

const LinkCard = styled.a`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 40px 32px 32px;
  border: 1px solid rgba(201,147,58,0.2);
  border-radius: 2px;
  background: linear-gradient(145deg, rgba(30,18,8,0.9), rgba(20,11,4,0.95));
  text-decoration: none;
  color: ${cream};
  transition: border-color 0.35s, transform 0.35s, box-shadow 0.35s;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, ${amber}, transparent);
    opacity: 0;
    transition: opacity 0.35s;
  }

  &:hover {
    border-color: rgba(201,147,58,0.55);
    transform: translateY(-5px);
    box-shadow: 0 20px 50px rgba(0,0,0,0.5);
    &::before { opacity: 1; }
  }
`

const CardNumber = styled.span`
  font-family: ${sans};
  font-size: 0.65rem;
  letter-spacing: 0.3em;
  color: ${amber};
  display: block;
  margin-bottom: 20px;
`

const CardTitle = styled.h4`
  font-family: ${display};
  font-size: 1.25rem;
  font-weight: 400;
  line-height: 1.35;
  margin: 0 0 20px;
  color: ${cream};
`

const CardMeta = styled.p`
  font-family: ${sans};
  font-size: 0.82rem;
  color: #8a7560;
  margin: 0 0 24px;
  line-height: 1.6;
`

const CardArrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: ${sans};
  font-size: 0.75rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${amber};

  &::after {
    content: '→';
    font-size: 1rem;
    transition: transform 0.3s;
  }

  ${LinkCard}:hover &::after { transform: translateX(5px); }
`

/* ══ MAP SECTION ═════════════════════════════════════════════════ */
const MapSectionOuter = styled.section`
  position: relative;
  background: linear-gradient(180deg, #120b05 0%, #0e0a07 100%);
  padding: 0 0 100px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(201,147,58,0.4), transparent);
  }
`

const MapSectionNoise = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.02;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-size: 200px;
  pointer-events: none;
`

const MapSectionGlow = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse 60% 40% at 50% 20%, rgba(201,147,58,0.06) 0%, transparent 60%);
  pointer-events: none;
`

const MapSectionInner = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
`

const MapHeader = styled.div`
  text-align: center;
  padding: 80px 0 52px;
`

const MapEyebrow = styled.p`
  font-family: ${sans};
  font-size: 0.68rem;
  letter-spacing: 0.35em;
  text-transform: uppercase;
  color: ${amber};
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;

  &::before, &::after {
    content: '';
    display: inline-block;
    width: 26px;
    height: 1px;
    background: ${amber};
    opacity: 0.4;
  }
`

const MapTitle = styled.h2`
  font-family: ${display};
  font-size: clamp(2.5rem, 5vw, 4.5rem);
  font-weight: 300;
  line-height: 1.05;
  color: ${cream};
  margin: 0 0 16px;
  letter-spacing: -0.3px;

  em { font-style: italic; color: ${amber}; }
`

const MapSubtitle = styled.p`
  font-family: ${sans};
  font-size: 0.88rem;
  font-weight: 300;
  letter-spacing: 2px;
  color: rgba(196,180,154,0.6);
  margin: 0;
`

const MapLayout = styled.div`
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 0;
  border: 1px solid rgba(201,147,58,0.18);
  border-radius: 12px;
  overflow: hidden;
  height: 680px;

  @media (max-width: 1024px) {
    grid-template-columns: 320px 1fr;
  }

  @media (max-width: 780px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`

/* ── Sidebar ──────────────────────────────────────────────────── */
const WineryList = styled.div`
  overflow-y: auto;
  background: #13100c;
  border-right: 1px solid rgba(201,147,58,0.15);
  scrollbar-width: thin;
  scrollbar-color: rgba(201,147,58,0.3) transparent;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: rgba(201,147,58,0.3);
    border-radius: 2px;
  }

  @media (max-width: 780px) {
    max-height: 320px;
  }
`

const HomeCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  background: rgba(201,147,58,0.08);
  border-bottom: 1px solid rgba(201,147,58,0.12);
`

const HomeIcon = styled.div`
  font-size: 1.6rem;
  flex-shrink: 0;
`

const HomeText = styled.div``

const HomeLabel = styled.div`
  font-family: ${sans};
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: ${amber};
  margin-bottom: 2px;
`

const HomeName = styled.div`
  font-family: ${display};
  font-size: 1rem;
  font-weight: 400;
  color: ${cream};
  line-height: 1.2;
`

const HomeAddr = styled.div`
  font-family: ${sans};
  font-size: 0.75rem;
  font-weight: 300;
  color: rgba(196,180,154,0.55);
`

const ListDivider = styled.div`
  height: 1px;
  background: rgba(201,147,58,0.1);
`

const WineryCard = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid rgba(201,147,58,0.08);
  cursor: pointer;
  transition: background 0.25s;
  background: ${p => p.$active ? 'rgba(201,147,58,0.07)' : 'transparent'};
  border-left: 3px solid ${p => p.$active ? amber : 'transparent'};
  position: relative;

  &:hover {
    background: rgba(201,147,58,0.05);
  }
`

const WineryCardTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`

const WineryNum = styled.div`
  font-family: ${slab};
  font-size: 1.2rem;
  color: rgba(201,147,58,0.25);
  line-height: 1;
  flex-shrink: 0;
  padding-top: 2px;
`

const WineryInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const WineryName = styled.div`
  font-family: ${sans};
  font-size: 0.9rem;
  font-weight: 500;
  color: ${p => p.$active ? amber : cream};
  margin-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.25s;
`

const WineryAddr = styled.div`
  font-family: ${sans};
  font-size: 0.72rem;
  font-weight: 300;
  color: rgba(196,180,154,0.5);
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const WineryTagRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`

const WineryTag = styled.span`
  font-family: ${sans};
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(201,147,58,0.7);
  padding: 2px 8px;
  border: 1px solid rgba(201,147,58,0.2);
  border-radius: 100px;
`

const WineryDist = styled.span`
  font-family: ${sans};
  font-size: 10px;
  color: rgba(196,180,154,0.4);
  margin-left: auto;
`

const WineryRating = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
`

const RatingStar = styled.span`
  font-size: 0.7rem;
  color: ${amber};
`

const RatingVal = styled.span`
  font-family: ${sans};
  font-size: 0.75rem;
  font-weight: 600;
  color: ${amber};
`

const WineryExpanded = styled.div`
  margin-top: 12px;
  overflow: hidden;
`

const ExpandedNotes = styled.p`
  font-family: ${sans};
  font-size: 0.8rem;
  font-weight: 300;
  line-height: 1.65;
  color: rgba(196,180,154,0.8);
  margin: 0 0 10px;
`

const ExpandedMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
`

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${sans};
  font-size: 0.75rem;
  font-weight: 300;
  color: rgba(196,180,154,0.65);
`

const MetaIcon = styled.span`
  font-size: 0.75rem;
`

const ExpandedActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const ExpandBtn = styled.a`
  display: inline-block;
  font-family: ${sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  text-decoration: none;
  padding: 7px 14px;
  border-radius: 100px;
  transition: all 0.25s;
  background: ${p => p.$outline ? 'transparent' : amber};
  color: ${p => p.$outline ? amber : '#0e0a07'};
  border: 1px solid ${p => p.$outline ? 'rgba(201,147,58,0.4)' : amber};

  &:hover {
    background: ${p => p.$outline ? 'rgba(201,147,58,0.12)' : amberLt};
    border-color: ${amberLt};
    color: ${p => p.$outline ? amberLt : '#0e0a07'};
  }
`

/* ── Map ──────────────────────────────────────────────────────── */
const MapWrap = styled.div`
  position: relative;
  height: 100%;

  .leaflet-container {
    height: 100%;
    width: 100%;
  }

  @media (max-width: 780px) {
    height: 420px;
  }
`

const MapLegend = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  z-index: 1000;
  background: rgba(18,11,5,0.88);
  border: 1px solid rgba(201,147,58,0.25);
  border-radius: 8px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  backdrop-filter: blur(8px);
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const LegendDot = styled.div`
  width: ${p => p.$home ? '14px' : '10px'};
  height: ${p => p.$home ? '14px' : '10px'};
  border-radius: 50%;
  background: ${p => p.$home ? amber : p.$active ? amber : 'transparent'};
  border: 2px solid ${amber};
  flex-shrink: 0;
`

const LegendText = styled.span`
  font-family: ${sans};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 1px;
  color: rgba(196,180,154,0.7);
`

/* ── Popup inner ──────────────────────────────────────────────── */
const PopupInner = styled.div`
  padding: 4px 2px;
  min-width: 200px;
`

const PopupLabel = styled.div`
  font-family: ${sans};
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${amber};
  margin-bottom: 4px;
`

const PopupName = styled.div`
  font-family: ${display};
  font-size: 1.1rem;
  font-weight: 400;
  color: ${cream};
  margin-bottom: 4px;
`

const PopupSub = styled.div`
  font-family: ${sans};
  font-size: 0.75rem;
  font-weight: 300;
  color: rgba(196,180,154,0.6);
  margin-bottom: 6px;
`

const PopupHours = styled.div`
  font-family: ${sans};
  font-size: 0.72rem;
  font-weight: 300;
  color: rgba(196,180,154,0.55);
  margin-bottom: 10px;
`

const PopupLinkRow = styled.div`
  display: flex;
  gap: 10px;
`

const PopupLink = styled.a`
  font-family: ${sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${amber};
  text-decoration: none;
  border-bottom: 1px solid rgba(201,147,58,0.3);
  padding-bottom: 1px;
  transition: color 0.2s, border-color 0.2s;

  &:hover {
    color: ${amberLt};
    border-color: ${amberLt};
  }
`

/* ── Tour Section ─────────────────────────────────────────────── */
const TourSection = styled.section`
  position: relative;
  padding: 0 24px 100px;
  background: #120b05;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(201,147,58,0.4), transparent);
  }
`

const TourInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`

const TourEyebrow = styled.p`
  font-family: ${sans};
  font-size: 0.68rem;
  letter-spacing: 0.35em;
  text-transform: uppercase;
  color: ${amber};
  text-align: center;
  padding-top: 80px;
  margin-bottom: 14px;
`

const TourTitle = styled.h2`
  font-family: ${display};
  font-size: clamp(1.8rem, 3.5vw, 2.9rem);
  font-weight: 400;
  text-align: center;
  color: ${cream};
  margin: 0 auto 16px;
  max-width: 700px;
  line-height: 1.2;
`

const TourDivider = styled.span`
  display: block;
  height: 1px;
  width: 72px;
  background: linear-gradient(90deg, transparent, ${amber}, transparent);
  margin: 0 auto 60px;
`

const TourLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  border: 1px solid rgba(201,147,58,0.18);
  border-radius: 2px;
  overflow: hidden;

  @media (max-width: 720px) { grid-template-columns: 1fr; }
`

const TourImagePane = styled.div`
  position: relative;
  min-height: 420px;
  overflow: hidden;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.7s ease;
    filter: brightness(0.88) saturate(1.1);
  }

  &:hover img { transform: scale(1.03); }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 60%, #120b05 100%);
    pointer-events: none;
  }
`

const TourTextPane = styled.div`
  padding: 56px 48px;
  background: linear-gradient(135deg, #1c1008, #120b05);
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 720px) { padding: 40px 28px; }
`

const TourBadge = styled.span`
  display: inline-block;
  font-family: ${sans};
  font-size: 0.68rem;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: #120b05;
  background: ${amber};
  padding: 6px 14px;
  border-radius: 1px;
  margin-bottom: 28px;
  align-self: flex-start;
`

const TourBody = styled.p`
  font-family: ${sans};
  font-size: 1rem;
  line-height: 1.85;
  color: ${bodyClr};
  margin: 0 0 32px;
`

const TourFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const TourFeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: ${sans};
  font-size: 0.88rem;
  color: #d8c9b2;

  &::before {
    content: '';
    display: inline-block;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${amber};
    flex-shrink: 0;
  }
`

/* ── Marquee ──────────────────────────────────────────────────── */
const MarqueeStrip = styled.div`
  background: ${amber};
  padding: 13px 0;
  overflow: hidden;
  white-space: nowrap;
`

const MarqueeInner = styled.div`
  display: inline-flex;
  animation: ${marquee} 28s linear infinite;
`

const MarqueeText = styled.span`
  font-family: ${slab};
  font-size: 0.95rem;
  letter-spacing: 3px;
  color: #0e0a07;
  padding: 0 28px;
`